import type { QueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
	Scripts,
	useRouteContext,
} from "@tanstack/react-router";
import type { TRPCOptionsProxy } from "@trpc/tanstack-react-query";
import React, { useEffect, useState, type ReactNode } from "react";
import { initializeSupportWidget } from "@/lib/support-widget";
import { Toaster } from "@/components/ui/sonner";
import { Provider as TRPCProvider } from "@/integrations/tanstack-query/root-provider";
import type { TRPCRouter } from "@/integrations/trpc/router";
import appCss from "../styles.css?url";

// Global Error Boundary with detailed logging
interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
	errorInfo: React.ErrorInfo | null;
}

class GlobalErrorBoundary extends React.Component<
	{ children: ReactNode },
	ErrorBoundaryState
> {
	constructor(props: { children: ReactNode }) {
		super(props);
		this.state = { hasError: false, error: null, errorInfo: null };
	}

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		// Detailed error logging
		console.error("=== GLOBAL ERROR BOUNDARY CAUGHT ERROR ===");
		console.error("Error:", error);
		console.error("Error name:", error.name);
		console.error("Error message:", error.message);
		console.error("Error stack:", error.stack);
		console.error("Component stack:", errorInfo.componentStack);
		console.error("=== END ERROR DETAILS ===");

		this.setState({ errorInfo });
	}

	render() {
		if (this.state.hasError) {
			return (
				<div style={{ padding: "20px", fontFamily: "monospace" }}>
					<h1 style={{ color: "red" }}>Something went wrong</h1>
					<h2>Error: {this.state.error?.message}</h2>
					<details style={{ whiteSpace: "pre-wrap", marginTop: "10px" }}>
						<summary>Error Stack</summary>
						<pre style={{ background: "#f0f0f0", padding: "10px", overflow: "auto" }}>
							{this.state.error?.stack}
						</pre>
					</details>
					<details style={{ whiteSpace: "pre-wrap", marginTop: "10px" }}>
						<summary>Component Stack</summary>
						<pre style={{ background: "#f0f0f0", padding: "10px", overflow: "auto" }}>
							{this.state.errorInfo?.componentStack}
						</pre>
					</details>
					<button
						onClick={() => window.location.reload()}
						style={{ marginTop: "20px", padding: "10px 20px", cursor: "pointer" }}
					>
						Reload Page
					</button>
				</div>
			);
		}

		return this.props.children;
	}
}

interface MyRouterContext {
	queryClient: QueryClient;

	trpc: TRPCOptionsProxy<TRPCRouter>;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
			{
				title: "Bidscope Purchasing",
			},
		],
		links: [
			{
				rel: "stylesheet",
				href: appCss,
			},
			{
				rel: "icon",
				type: "image/png",
				href: "/favicon.png",
			},
		],
		scripts: [],
	}),

	shellComponent: RootDocument,
	component: RootComponent,
});

// Shell component: Just the HTML structure, no client-side code
function RootDocument({ children }: { children: ReactNode }) {
	return (
		<html lang="en">
			<head>
				<HeadContent />
			</head>
			<body>
				{children}
				<Scripts />
			</body>
		</html>
	);
}

// Root component: Client-side providers and Outlet
function RootComponent() {
	const { queryClient } = useRouteContext({ from: "__root__" });

	// Set hydration marker for e2e tests
	useEffect(() => {
		document.body.setAttribute("data-hydrated", "true");
		return () => {
			document.body.removeAttribute("data-hydrated");
		};
	}, []);

	// Initialize Gleap support widget (skip on embed pages)
	useEffect(() => {
		// Don't show Gleap widget on embed pages
		if (typeof window !== "undefined" && window.location.pathname.endsWith("/embed")) {
			return;
		}
		initializeSupportWidget();
	}, []);

	// Global error handlers for uncaught errors
	useEffect(() => {
		const handleError = (event: ErrorEvent) => {
			console.error("=== GLOBAL UNCAUGHT ERROR ===");
			console.error("Message:", event.message);
			console.error("Filename:", event.filename);
			console.error("Line:", event.lineno, "Col:", event.colno);
			console.error("Error object:", event.error);
			console.error("Stack:", event.error?.stack);
			console.error("=== END UNCAUGHT ERROR ===");
		};

		const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
			console.error("=== UNHANDLED PROMISE REJECTION ===");
			console.error("Reason:", event.reason);
			console.error("Stack:", event.reason?.stack);
			console.error("=== END REJECTION ===");
		};

		window.addEventListener("error", handleError);
		window.addEventListener("unhandledrejection", handleUnhandledRejection);

		return () => {
			window.removeEventListener("error", handleError);
			window.removeEventListener("unhandledrejection", handleUnhandledRejection);
		};
	}, []);

	return (
		<GlobalErrorBoundary>
			<TRPCProvider queryClient={queryClient}>
				<ClientAuthProvider>
					<Outlet />
					<Toaster position="top-right" />
				</ClientAuthProvider>
			</TRPCProvider>
		</GlobalErrorBoundary>
	);
}

// Client-only AuthProvider wrapper using dynamic import
function ClientAuthProvider({ children }: { children: ReactNode }) {
	const [AuthProvider, setAuthProvider] = useState<
		React.ComponentType<{ children: ReactNode }> | null
	>(null);
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
		// Dynamic import only runs on client
		import("@/lib/auth").then((mod) => {
			setAuthProvider(() => mod.AuthProvider);
		});
	}, []);

	// On server, render children without provider (SSR)
	if (!isClient) {
		return <>{children}</>;
	}

	// On client, wait for AuthProvider to load before rendering children
	if (!AuthProvider) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
			</div>
		);
	}

	return <AuthProvider>{children}</AuthProvider>;
}
